import { LengthRange } from '../types/lengthRange';

export const getLaneFromPosition = (
	lanes: LengthRange[],
	getLaneYLocation: (lane: LengthRange) => number | undefined,
	laneHeight: number,
	y: number,
): LengthRange | undefined => {
	return lanes.find((r) => {
		const laneY = getLaneYLocation(r);
		return laneY !== undefined && laneY < y && y < laneY + laneHeight;
	});
};

export const calculateCompletelyVisibleCoreRange = (
	totalDrilledCoreRange: LengthRange,
	viewPortResolution: number,
	viewPortWidth?: number,
	viewPortHeight?: number,
	viewPortDrilledCoreRange?: LengthRange,
	visualDrillHoleStartOffset?: number,
): LengthRange => {
	if (!viewPortWidth) return totalDrilledCoreRange;
	if (!viewPortHeight) return totalDrilledCoreRange;
	if (!viewPortDrilledCoreRange) return totalDrilledCoreRange;
	const rowLength = viewPortWidth / viewPortResolution;
	let currentStart = totalDrilledCoreRange.depthFrom - (visualDrillHoleStartOffset ?? 0);
	let rowsStart = currentStart;
	const endRows = Math.min(viewPortDrilledCoreRange.depthTo, totalDrilledCoreRange.depthTo);
	while (currentStart < endRows) {
		//If we haven't reached the start of our viewport, increment and carry on
		if (currentStart < viewPortDrilledCoreRange.depthFrom - rowLength) {
			rowsStart = currentStart += rowLength;
			continue;
		}
		currentStart += rowLength;
	}
	return {
		depthFrom: Math.max(rowsStart, totalDrilledCoreRange.depthFrom),
		depthTo: Math.min(currentStart, totalDrilledCoreRange.depthTo),
	};
};

/** Viewport visible range considers lanes that are at least slightly visible (with tolerance) in the viewport. Otherwise, it is too hard to see mouse hovers in the UX */
export const calculateViewportVisibleCoreRange = (
	totalDrilledCoreRange: LengthRange,
	viewPortResolution: number,
	viewPortWidth?: number,
	viewPortHeight?: number,
	viewPortDrilledCoreRange?: LengthRange,
	visualDrillHoleStartOffset?: number,
	tolerance: number = 0.15, // betwen 0 and 1
): LengthRange => {
	if (!viewPortWidth) return totalDrilledCoreRange;
	if (!viewPortHeight) return totalDrilledCoreRange;
	if (!viewPortDrilledCoreRange) return totalDrilledCoreRange;
	const rowLength = viewPortWidth / viewPortResolution;
	let currentStart = totalDrilledCoreRange.depthFrom - (visualDrillHoleStartOffset ?? 0);
	let rowsStart = currentStart;
	const endRows = Math.min(viewPortDrilledCoreRange.depthTo, totalDrilledCoreRange.depthTo);
	while (currentStart - rowLength * -tolerance < endRows) {
		//If we haven't reached the start of our viewport, increment and carry on
		if ((viewPortDrilledCoreRange.depthFrom - currentStart) / rowLength > 1 - tolerance) {
			rowsStart = currentStart += rowLength;
			continue;
		}
		currentStart += rowLength;
	}
	return {
		depthFrom: Math.max(rowsStart, totalDrilledCoreRange.depthFrom),
		depthTo: Math.min(currentStart, totalDrilledCoreRange.depthTo),
	};
};
